import { ScrollLock } from "./dialog";
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { Move, ZoomIn } from "lucide-react";
import { btnPrimary, btnSecondary } from "./admin-ui";

/** Output side, in pixels, of the square avatar we render to storage. */
const OUTPUT = 512;

type Point = { x: number; y: number };

/**
 * Facebook-style avatar cropper: the frame stays put, you drag the photo behind
 * it and zoom to pick exactly which square becomes the avatar. Pointer events, so
 * it works the same with a mouse or a finger. "Aplicar" renders the visible
 * square to a canvas and hands back a JPEG blob — no server round-trip to crop.
 */
export function AvatarCropModal({
  file,
  busy,
  onCancel,
  onApply,
}: {
  file: File;
  busy?: boolean;
  onCancel: () => void;
  onApply: (blob: Blob) => void;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [src, setSrc] = useState("");
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [frame, setFrame] = useState(0); // rendered frame side, px
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState<Point>({ x: 0, y: 0 });
  const drag = useRef<{
    px: number;
    py: number;
    ox: number;
    oy: number;
  } | null>(null);

  // Own the object URL for the selected file; revoke it when we're done.
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // At zoom 1 the photo just covers the frame; larger zoom crops in tighter.
  const baseScale = nat && frame ? Math.max(frame / nat.w, frame / nat.h) : 1;
  const dispW = nat ? nat.w * baseScale * zoom : 0;
  const dispH = nat ? nat.h * baseScale * zoom : 0;

  const clamp = (p: Point, w: number, h: number): Point => ({
    x: Math.min(0, Math.max(frame - w, p.x)),
    y: Math.min(0, Math.max(frame - h, p.y)),
  });

  const onLoad = () => {
    const img = imgRef.current;
    const side = frameRef.current?.clientWidth ?? 0;
    if (!img || !side) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const scale = Math.max(side / w, side / h);
    setNat({ w, h });
    setFrame(side);
    setZoom(1);
    // Centre the photo in the frame.
    setPos({ x: (side - w * scale) / 2, y: (side - h * scale) / 2 });
  };

  const onZoom = (next: number) => {
    if (!nat) return;
    const w = nat.w * baseScale * next;
    const h = nat.h * baseScale * next;
    // Zoom around the frame's centre so the photo doesn't lurch sideways.
    const ratio = next / zoom;
    const centred = {
      x: frame / 2 - (frame / 2 - pos.x) * ratio,
      y: frame / 2 - (frame / 2 - pos.y) * ratio,
    };
    setZoom(next);
    setPos(clamp(centred, w, h));
  };

  const onDown = (e: PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, ox: pos.x, oy: pos.y };
  };
  const onMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    setPos(
      clamp(
        { x: d.ox + (e.clientX - d.px), y: d.oy + (e.clientY - d.py) },
        dispW,
        dispH,
      ),
    );
  };
  const onUp = () => {
    drag.current = null;
  };

  const apply = () => {
    const img = imgRef.current;
    if (!img || !nat || !frame) return;
    const s = baseScale * zoom; // displayed px per source px
    const sx = -pos.x / s;
    const sy = -pos.y / s;
    const sSize = frame / s;
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
    canvas.toBlob(
      (blob) => {
        if (blob) onApply(blob);
      },
      "image/jpeg",
      0.9,
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[110] grid place-items-center overscroll-contain p-4">
      <ScrollLock />
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onCancel}
        className="bg-ink/60 absolute inset-0"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 420, damping: 30 }}
        role="dialog"
        aria-modal="true"
        aria-label="Ajustar foto de perfil"
        className="border-ink bg-bone shadow-brutal border-surface relative w-[min(92vw,380px)] p-5"
      >
        <p className="font-mono text-xs font-bold uppercase tracking-wider">
          Foto de perfil
        </p>
        <h2 className="font-display mt-1 text-2xl font-extrabold leading-none">
          Ajusta el recorte
        </h2>
        <p className="text-ink-soft mt-1 flex items-center gap-1.5 text-sm">
          <Move size={14} /> Arrastra la foto y usa el zoom.
        </p>

        <div
          ref={frameRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          className="border-ink bg-paper border-surface relative mx-auto mt-4 aspect-square w-[min(300px,72vw)] touch-none select-none overflow-hidden"
          style={{ cursor: drag.current ? "grabbing" : "grab" }}
        >
          {src && (
            <img
              ref={imgRef}
              src={src}
              alt=""
              draggable={false}
              onLoad={onLoad}
              className="pointer-events-none absolute left-0 top-0 max-w-none"
              style={{
                width: dispW || undefined,
                height: dispH || undefined,
                transform: `translate(${pos.x}px, ${pos.y}px)`,
              }}
            />
          )}
          {/* A thin inner frame so the crop edges read clearly against the photo. */}
          <div className="border-bone/70 border-chip pointer-events-none absolute inset-0" />
        </div>

        <label className="mt-4 flex items-center gap-3">
          <ZoomIn size={16} className="shrink-0" />
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => onZoom(Number(e.target.value))}
            disabled={!nat}
            className="accent-ink h-1.5 w-full"
            aria-label="Zoom"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={`${btnSecondary} px-4 py-2 text-sm`}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={busy || !nat}
            className={`${btnPrimary} px-4 py-2 text-sm`}
          >
            {busy ? "Aplicando…" : "Aplicar"}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}
